/**
 * Approval stages, mirroring nova-api's utils/approvalStage.js.
 *
 * DUPLICATED ACROSS REPOS ON PURPOSE -- they are separate deployables with no
 * shared package. The API decides and stores the stage; this file only names
 * the integers so the UI never compares a bare number. If you change one file,
 * change the other: the numbers are a wire contract.
 *
 * The point of the column is that the UI stops re-deriving this from the
 * approval cards. Two independent derivations of one fact is why an order
 * could read Pending in the tab and Approved in its badge on the same screen.
 */
export const APPROVAL_STAGE = Object.freeze({
  NOT_SENT: 1,
  PENDING: 2,
  CHANGES_REQUESTED: 3,
  ADMIN_APPROVAL: 4,
  APPROVED: 5,
});

/** Tab order is the order work actually flows in. */
export const APPROVAL_TABS = [
  "not_sent",
  "pending",
  "changes_requested",
  "admin_approval",
  "approved",
  "all",
];

export const APPROVAL_TAB_LABELS = Object.freeze({
  not_sent: "Not Sent",
  pending: "Pending",
  changes_requested: "Changes Requested",
  admin_approval: "Admin Approval",
  approved: "Approved",
  all: "All",
});

/**
 * The stage of a row, whichever shape the row arrives in.
 *
 * Two shapes carry it, because two kinds of list show this badge:
 *   - APPROVAL rows (the Approvals list) nest the order as `WorkOrder`
 *   - WORK ORDER rows (work orders list, quick view, job board) are flat and
 *     nest the approval as `woLotApproval`
 *
 * Missing reads as Not Sent rather than undefined. Absent is not a sixth
 * state, and an order with no approval at all is exactly stage 1 -- but note
 * that a MISSING column reads the same way, which is why every query feeding
 * one of these lists has to select approvalStage. Two of them restrict their
 * attributes and needed it added by hand.
 */
export const stageOfRow = (row) =>
  Number(row?.WorkOrder?.approvalStage ?? row?.approvalStage) ||
  APPROVAL_STAGE.NOT_SENT;

/**
 * The admin-reviewed values. ALL FOUR, on EVERY tab, at every stage.
 *
 * This used to be two lists: approve and reject were withheld below stage 4,
 * leaving only pending/hold on the Not Sent, Pending and Changes Requested
 * tabs. Unblocked on request -- the server's matching 409 guard went with it,
 * so there is no longer a stage this list has to be filtered by.
 *
 * Setting Approved on an early order does not promote it. The stage reads
 * adminReviewed only once the customer has finished (see stageOf in the API),
 * so the value is recorded, the order stays in its tab, and it takes effect as
 * a pre-approval when the customer is done.
 */
export const ADMIN_REVIEW_ALL = ["pending", "approved", "hold", "rejected"];

const norm = (v) => (v || "").toString().trim().toLowerCase();

/**
 * Has anything on this order been rejected?
 *
 * Rejected and partly-answered are BOTH stage 3, so this is a sub-distinction
 * inside one stage rather than a rival definition of the stage itself.
 *
 * `hasRejected` is already computed and stored server-side, and covers
 * products, excel and shipping. The extra checks are belt-and-braces for a
 * stale stored flag -- the backfill found real rows whose stored booleans
 * disagreed with their actual state, and showing a rejected order as anything
 * else is the worst way to be wrong here.
 */
export const hasRejectedOn = (row) => {
  const appr = row?.woLotApproval ?? row;
  if (appr?.hasRejected) return true;
  if (norm(appr?.excelFileStatus) === "rejected") return true;
  if (norm(appr?.shippingAddressStatus) === "rejected") return true;

  const products = row?.WorkOrder?.WOProducts ?? row?.WOProducts;
  return !!products?.some?.((p) => norm(p?.approveStatus) === "rejected");
};

/**
 * Which badge an order gets, as a TONE rather than CSS.
 *
 * One definition of the decision; each screen keeps its own classes, because
 * they differ slightly and unifying the styling is not what this is for.
 *
 * Before the stage column this decision existed in FIVE places -- the
 * Approvals list (twice), the work orders list, and the quick view -- each
 * re-deriving it from the approval cards, and each free to disagree with the
 * others and with the tab query. That is exactly how an order came to read
 * Approved in one column and Pending in another on the same screen.
 *
 * "not_sent" is returned separately from "pending" because the screens
 * genuinely disagree about it: the work orders list and quick view show a grey
 * "Not sent", the Approvals list shows it as Pending. Both are reasonable in
 * context, so the TONE is shared and the wording stays local. Collapsing them
 * here would silently restyle the Approvals list.
 *
 * @returns {"rejected"|"approved"|"partial"|"not_sent"|"pending"}
 */
export const approvalBadgeTone = (row) => {
  if (hasRejectedOn(row)) return "rejected";

  const stage = stageOfRow(row);
  if (stage >= APPROVAL_STAGE.ADMIN_APPROVAL) return "approved";
  if (stage === APPROVAL_STAGE.CHANGES_REQUESTED) return "partial";
  if (stage === APPROVAL_STAGE.NOT_SENT) return "not_sent";
  return "pending";
};
